import { useState, useEffect, useRef } from 'react';

const MAX_TRACKED_USERS = 100;
const IRC_WS_URL = 'wss://irc-ws.chat.twitch.tv:443';
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY = 3000;

export default function useTwitchChat(channelName = 'itsflannelbeard') {
  const normalizedChannel = channelName?.toLowerCase().trim() || 'itsflannelbeard';
  const [activeUsers, setActiveUsers] = useState([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const usersSetRef = useRef(new Set());
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isConnectedRef = useRef(false);
  const capAcknowledgedRef = useRef(false);
  const hasJoinedRef = useRef(false);
  const monitorBotUsernameRef = useRef(null);

  useEffect(() => {
    if (!normalizedChannel) return;

    const usersSet = usersSetRef.current;

    const flushUsers = () => {
      const limited = Array.from(usersSetRef.current).slice(0, MAX_TRACKED_USERS);
      setActiveUsers(limited);
    };

    const tryJoinChannel = (ws) => {
      if (isConnectedRef.current && capAcknowledgedRef.current && !hasJoinedRef.current) {
        ws.send(`JOIN #${normalizedChannel}\r\n`);
      }
    };

    const connect = () => {
      try {
        const ws = new WebSocket(IRC_WS_URL);
        wsRef.current = ws;
        capAcknowledgedRef.current = false;
        hasJoinedRef.current = false;

        ws.onopen = () => {
          reconnectAttemptsRef.current = 0;

          // Anonymous read-only connection — no PASS/auth needed for justinfan nicknames
          const nick = `justinfan${Math.floor(Math.random() * 1000000)}`;
          monitorBotUsernameRef.current = nick;

          ws.send(`NICK ${nick}\r\n`);
          ws.send('CAP REQ :twitch.tv/membership twitch.tv/tags twitch.tv/commands\r\n');
        };

        ws.onmessage = (event) => {
          const messages = event.data.split('\r\n').filter(msg => msg.trim());

          messages.forEach(message => {
            if (message.startsWith('PING')) {
              ws.send('PONG :tmi.twitch.tv\r\n');
              return;
            }

            if (message.includes('CAP * ACK')) {
              capAcknowledgedRef.current = true;
              tryJoinChannel(ws);
              return;
            }

            // 001-004 are IRC welcome messages
            const welcomeMatch = message.match(/^:tmi\.twitch\.tv (001|002|003|004)/);
            if (welcomeMatch) {
              if (!isConnectedRef.current) {
                console.log(`[Twitch IRC] Connected to #${normalizedChannel}`);
                isConnectedRef.current = true;
                setConnected(true);
                tryJoinChannel(ws);
              }
              return;
            }

            // :user!user@user.tmi.twitch.tv JOIN #channel
            const joinMatch = message.match(/:(.+)!.+@.+\.tmi\.twitch\.tv JOIN #(.+)/);
            if (joinMatch) {
              const username = joinMatch[1].toLowerCase();
              const channel = joinMatch[2].toLowerCase();

              if (username.startsWith('justinfan') && channel === normalizedChannel && !hasJoinedRef.current) {
                console.log(`[Twitch IRC] Joined #${normalizedChannel} as ${username}`);
                hasJoinedRef.current = true;
                setTimeout(() => {
                  if (wsRef.current?.readyState === WebSocket.OPEN) {
                    ws.send(`NAMES #${normalizedChannel}\r\n`);
                  }
                }, 500);
                return;
              }

              if (channel === normalizedChannel && username && !username.startsWith('justinfan') && !usersSetRef.current.has(username)) {
                usersSetRef.current.add(username);
                flushUsers();
              }
              return;
            }

            // :user!user@user.tmi.twitch.tv PART #channel
            const partMatch = message.match(/:(.+)!.+@.+\.tmi\.twitch\.tv PART #(.+)/);
            if (partMatch) {
              const username = partMatch[1].toLowerCase();
              if (username && usersSetRef.current.has(username)) {
                usersSetRef.current.delete(username);
                flushUsers();
              }
              return;
            }

            // NAMES list: :tmi.twitch.tv 353 user = #channel :user1 user2 ...
            if (message.includes('353')) {
              const namesMatch = message.match(/353 .+ #.+ :(.+)/);
              if (namesMatch) {
                const names = namesMatch[1].trim().split(/\s+/).map(name => name.replace('@', '').toLowerCase());
                names.forEach(name => {
                  if (name && !name.startsWith('justinfan')) {
                    usersSetRef.current.add(name);
                  }
                });
                flushUsers();
              }
              return;
            }

            // End of NAMES list (366)
            if (message.includes('366')) {
              flushUsers();
              return;
            }

            // @tags :user!user@user.tmi.twitch.tv PRIVMSG #channel :message
            if (message.includes('PRIVMSG')) {
              const tagsMatch = message.match(/@([^ ]+)/);
              const usernameMatch = tagsMatch ? tagsMatch[1].match(/display-name=([^;]+)/) : null;
              const prefixMatch = message.match(/:([^!]+)!/);
              const username = usernameMatch
                ? usernameMatch[1].toLowerCase()
                : (prefixMatch ? prefixMatch[1].toLowerCase() : null);

              if (username && !username.startsWith('justinfan') && !usersSetRef.current.has(username)) {
                usersSetRef.current.add(username);
                flushUsers();
              }
              return;
            }
          });
        };

        ws.onerror = (error) => {
          console.error('[Twitch IRC] WebSocket error:', error);
          isConnectedRef.current = false;
          setConnected(false);
        };

        ws.onclose = (event) => {
          console.log(`[Twitch IRC] Disconnected (code=${event.code}, clean=${event.wasClean})`);
          isConnectedRef.current = false;
          setConnected(false);
          hasJoinedRef.current = false;
          capAcknowledgedRef.current = false;

          if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
            reconnectAttemptsRef.current++;
            console.log(`[Twitch IRC] Reconnecting (attempt ${reconnectAttemptsRef.current})...`);
            reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY);
          } else {
            console.error('[Twitch IRC] Max reconnection attempts reached');
          }
        };
      } catch (error) {
        console.error('[Twitch IRC] Failed to connect:', error);
        isConnectedRef.current = false;
        setConnected(false);
      }
    };

    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      usersSet.clear();
      setActiveUsers([]);
      isConnectedRef.current = false;
      setConnected(false);
      monitorBotUsernameRef.current = null;
    };
  }, [normalizedChannel]);

  return {
    activeUsers,
    connected,
    userCount: activeUsers.length,
    monitorBotUsername: monitorBotUsernameRef.current
  };
}

